import type { Character } from '../schemas/character';
import type { Move } from '../schemas/move';
import { commonMoves } from '../data/moves/common';
import { arePrerequisitesSatisfied, hasAccessToMulticlassCommonMoves } from './levelUp';
import { hasMoveRefInCharacter } from './moveLookup';

/**
 * **D10 — FI-11**: filtro disponibilità delle Mosse Comuni durante il level-up.
 *
 * Estratto da `services/levelUp.ts` per rispettare `max-lines: 400` quando
 * D10 ha aggiunto le 5 mosse multiclasse comuni a catena.
 *
 * Le mosse comuni si dividono in:
 *  - **Infanzia Complessa** (`common.infanzia-complessa`): disponibile SOLO
 *    al passaggio L1→L2 E se il PG non ha già `secondaryArchetypeId`.
 *  - **5 mosse multiclasse a catena** (Apprendista → Adepto → Esperto →
 *    Maestro → Gran Maestro): disponibili SOLO se il PG ha scelto
 *    `pastTen ∈ {'apprendista', 'instancabile'}` E i prerequisiti di catena
 *    sono soddisfatti.
 *
 * Pattern SSOT: l'unico punto in cui si decide "questa mossa comune è
 * disponibile al level-up corrente". Consumer: `validateMoveAvailability`
 * in `levelUp.ts` + UI `LevelUpStepMove` per il pool da mostrare.
 */

/**
 * **D10 — FI-11**: id delle 5 mosse comuni multiclasse a catena, disponibili
 * solo post-10° per Apprendista/Instancabile.
 */
export const POST_TEN_COMMON_MOVE_IDS = new Set<string>([
  'common.apprendista-multiclasse',
  'common.adepto-multiclasse',
  'common.esperto-multiclasse',
  'common.maestro-multiclasse',
  'common.gran-maestro-multiclasse',
]);

/**
 * Mosse comuni disponibili al level-up verso `targetLevel` per il `character`.
 * Filtro applicato a tutte le mosse del catalogo `commonMoves`.
 */
export function getAvailableCommonMoves(
  character: Character,
  targetLevel: number,
): readonly Move[] {
  return commonMoves.filter((move) => isCommonMoveAvailable(move, character, targetLevel));
}

function isCommonMoveAvailable(move: Move, character: Character, targetLevel: number): boolean {
  if (move.id === 'common.infanzia-complessa') {
    return targetLevel === 2 && character.secondaryArchetypeId === undefined;
  }
  if (POST_TEN_COMMON_MOVE_IDS.has(move.id)) {
    if (!hasAccessToMulticlassCommonMoves(character)) return false;
    if (hasMoveRefInCharacter(character, move.id)) return false;
    const ownedIds = new Set(character.moves.map((m) => m.id));
    return arePrerequisitesSatisfied(move.prerequisites, ownedIds);
  }
  return false;
}
